import React, { useEffect, useCallback, useRef, useState } from "react";

import { sudokuApi } from "../../api/sudokuApi";
import { statsApi } from "../../api/statsApi";
import { formatSeconds } from "../leaderboard/formatSeconds";
import { useSudokuGame } from "./hooks/useSudokuGame";
import { useSudokuTimer } from "./hooks/useSudokuTimer";
import { useSudokuSession } from "./hooks/useSudokuSession";
import useElementHeight from "../../hooks/useElementHeight";

import SudokuBoard from "./SudokuBoard";
import SudokuControls from "./SudokuControls";
import SudokuStatusBar from "./SudokuStatusBar";
import SudokuResultModal from "./SudokuResultModal";
import GameBoardFrame, { useGameBoardFrameMetrics } from "../games/GameBoardFrame";
// TEMP DEBUG -- delete this import and its usage below once the mobile Safari
// viewport investigation is done. See DebugViewportOverlay.jsx.
import DebugViewportOverlay, { isDebugOverlayEnabled } from "../games/DebugViewportOverlay";

const DIFFICULTIES = ["easy", "medium", "hard", "expert"];

export default function SudokuPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [difficulty, setDifficulty] = useState("medium");

  const { state, dispatch, conflictSet, peerSet, selectedValue } = useSudokuGame();
  const isRunning = state.status === "playing";

  const difficultyBarRef = useRef(null);
  const controlsRef = useRef(null);
  // Real available height between the header and bottom nav (same measurement
  // GameBoardFrame uses to position itself), minus the actual rendered heights of the
  // chrome around the board (difficulty/stats bar, number pad) and the gaps/space
  // between them. Sized this way -- against real space, not a sibling-gap proxy that
  // ends up measuring the board's own current size -- the board only shrinks below its
  // natural min(90vw, 620px) size on the rare short viewport where it actually needs
  // to, instead of ever rendering taller than what's visible below the header.
  const { frameHeight } = useGameBoardFrameMetrics();
  const difficultyBarHeight = useElementHeight(difficultyBarRef);
  const controlsHeight = useElementHeight(controlsRef);
  const GROUP_GAP_PX = 12; // matches GameBoardFrame's inner `gap-3` below the `sm` breakpoint
  const NEW_PUZZLE_LINK_RESERVE_PX = 28; // "New puzzle" link + its gap, only shown once playing
  const boardMaxHeightPx =
    frameHeight != null
      ? Math.max(
          0,
          frameHeight - difficultyBarHeight - controlsHeight - GROUP_GAP_PX * 2 - NEW_PUZZLE_LINK_RESERVE_PX
        )
      : null;
  const boardMaxWidthStyle =
    boardMaxHeightPx ? `min(90vw, 620px, ${boardMaxHeightPx}px)` : "min(90vw, 620px)";

  const [myBests, setMyBests] = useState({});
  const refreshMyBests = useCallback(() => {
    statsApi.getMySudokuBests().then(setMyBests).catch(() => {});
  }, []);
  useEffect(() => { refreshMyBests(); }, [refreshMyBests]);
  useEffect(() => {
    if (state.status === "won") refreshMyBests();
  }, [state.status, refreshMyBests]);

  const { elapsed, formatted: timerFormatted } = useSudokuTimer(
    state.savedElapsed ?? 0,
    isRunning
  );

  const { scheduleSave } = useSudokuSession(state.sessionId);

  const startNewGame = useCallback(
    async (diff = difficulty) => {
      setLoading(true);
      setError(null);
      try {
        const sessionData = await sudokuApi.newPuzzle(diff);
        dispatch({ type: "LOAD", sessionData });
      } catch {
        setError("Failed to load puzzle. Check your connection.");
      } finally {
        setLoading(false);
      }
    },
    [difficulty, dispatch]
  );

  useEffect(() => {
    startNewGame(difficulty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save on board change
  useEffect(() => {
    if (state.status === "idle" || !state.sessionId) return;
    const completed = state.status === "won";
    scheduleSave(state.board, {}, elapsed, state.mistakes, completed);
  }, [state.board, state.mistakes, state.status, elapsed, scheduleSave, state.sessionId]);

  const handleCellClick = useCallback(
    (idx) => dispatch({ type: "SELECT", idx }),
    [dispatch]
  );

  const handleNumber = useCallback(
    (num) => {
      if (state.selected === null) return;
      dispatch({ type: "ENTER", idx: state.selected, num });
    },
    [state.selected, dispatch]
  );

  const handleErase = useCallback(
    () => dispatch({ type: "ERASE", idx: state.selected }),
    [state.selected, dispatch]
  );

  const handleToggleNotes = useCallback(
    () => dispatch({ type: "TOGGLE_NOTES" }),
    [dispatch]
  );

  const handleDifficultyChange = useCallback(
    (diff) => {
      setDifficulty(diff);
      startNewGame(diff);
    },
    [startNewGame]
  );

  const handlePlayAgain = useCallback(
    () => startNewGame(difficulty),
    [startNewGame, difficulty]
  );

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key >= "1" && e.key <= "9") handleNumber(parseInt(e.key, 10));
      else if (e.key === "Backspace" || e.key === "Delete") handleErase();
      else if (e.key === "n" || e.key === "N") dispatch({ type: "TOGGLE_NOTES" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleNumber, handleErase, dispatch]);


  return (
    <>
      {/* TEMP DEBUG -- delete this line once the mobile Safari viewport investigation is done */}
      {isDebugOverlayEnabled() && <DebugViewportOverlay />}
      <GameBoardFrame>
        {/* Loading / error states */}
        {loading && (
          <div className="text-text-secondary text-sm py-2">Generating puzzle…</div>
        )}
        {error && !loading && (
          <div className="text-brand-rose text-sm py-4">{error}</div>
        )}

        <div className="w-full flex flex-col items-center">
          {/* Difficulty picker + status info, attached flush on top of the board */}
          <div
            ref={difficultyBarRef}
            className="
              w-full mx-auto flex flex-col gap-2
              rounded-t-lg border-2 border-b-0 border-brand-cyan/40
              bg-surface backdrop-blur
              px-3 pt-2.5 pb-2
              [@media(min-width:768px)_and_(max-height:700px)]:pt-1.5
              [@media(min-width:768px)_and_(max-height:700px)]:pb-1
              [@media(min-width:768px)_and_(max-height:700px)]:gap-1
            "
            style={{ maxWidth: boardMaxWidthStyle }}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="grid w-full grid-cols-4 gap-1 sm:flex sm:w-auto">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => handleDifficultyChange(d)}
                    disabled={loading}
                    className={[
                      "px-2.5 py-1 rounded-lg text-xs font-semibold capitalize transition focus:outline-none",
                      difficulty === d
                        ? "border border-brand-cyan/40 bg-brand-cyan/12 text-brand-cyan"
                        : "border border-border-soft bg-transparent text-text-muted hover:text-text-secondary",
                    ].join(" ")}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-text-faint shrink-0">
                Best: <span className="text-brand-cyan font-semibold">{formatSeconds(myBests[difficulty])}</span>
              </div>
            </div>

            {state.board.length > 0 && (
              <SudokuStatusBar
                timerFormatted={timerFormatted}
                mistakes={state.mistakes}
              />
            )}
          </div>

          {/* Board */}
          {state.board.length > 0 ? (
            <SudokuBoard
              board={state.board}
              selected={state.selected}
              conflictSet={conflictSet}
              peerSet={peerSet}
              selectedValue={selectedValue}
              onCellClick={handleCellClick}
              maxWidthStyle={boardMaxWidthStyle}
            />
          ) : (
            <div
              className="
                grid grid-cols-9
                border-2 border-t-0 border-brand-cyan/20
                rounded-b-lg overflow-hidden
                w-full shrink-0
                mx-auto aspect-square bg-surface
              "
              style={{ maxWidth: boardMaxWidthStyle }}
            >
              {Array.from({ length: 81 }).map((_, idx) => (
                <div
                  key={idx}
                  className="aspect-square border border-border-soft/40"
                />
              ))}
            </div>
          )}
        </div>

        <div ref={controlsRef} className="w-full flex flex-col items-center">
          {state.board.length > 0 ? (
            <SudokuControls
              notesMode={state.notesMode}
              onNumber={handleNumber}
              onErase={handleErase}
              onToggleNotes={handleToggleNotes}
              maxWidthStyle={boardMaxWidthStyle}
            />
          ) : (
            <div className="w-full mx-auto min-h-[96px]" style={{ maxWidth: boardMaxWidthStyle }} />
          )}
        </div>

        {/* New game button */}
        {!loading && state.board.length > 0 && state.status === "playing" && (
          <button
            type="button"
            onClick={handlePlayAgain}
            className="
              text-xs text-text-faint hover:text-text-secondary
              underline underline-offset-2 transition
            "
          >
            New puzzle
          </button>
        )}
      </GameBoardFrame>

      {/* Result modal */}
      <SudokuResultModal
        status={state.status}
        timerFormatted={timerFormatted}
        onPlayAgain={handlePlayAgain}
      />
    </>
  );
}
